import { LocalDate } from "@js-joda/core";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { type TimeReportsV2Filter } from "services/api/company/timeReports.service";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { formatSummaryDuration } from "web-apps/company/utils/timeAndDate";
import { TimeReportsSummaryCards } from "./components/common/summaryCards/timeReportsSummaryCards.component";
import { TimeReportsSummaryCard } from "./components/common/summaryCards/timeReportsSummaryCards.types";
import { useCompanyFilterOptions } from "web-apps/company/components/common/hooks/useCompanyFilterOptions";
import { TimeReportsOptionsPanel } from "./components/optionsPanel/timeReportsOptionsPanel.component";
import { PersonView } from "./components/views/person/personView.component";
import { StaffRegisterView } from "./components/views/staffRegister/staffRegisterView.component";
import { StaffRegisterCompanyCard } from "./components/views/staffRegister/staffRegisterCompanyCard.component";
import { StaffRegisterExportModal } from "./components/views/staffRegister/staffRegisterExportModal.component";
import { TimeReportsToolbar } from "./components/toolbar/timeReportsToolbar.component";
import {
  ViewTab,
  ViewTabs,
} from "./components/toolbar/timeReportsToolbar.styled";
import {
  Layout,
  PeriodSlot,
  StaffHeaderCompanyCardSlot,
  StaffHeaderDateCard,
  StaffHeaderRow,
  SummaryStrip,
  ViewSelectorRow,
  ViewSelectorActions,
} from "./timeReports.styled";
import { useTimeReportsV2Store } from "web-apps/company/stores/timeReportsV2/timeReportsV2Store";
import { useTimeReportsPreferencesSync } from "./hooks/useTimeReportsPreferencesSync";
import { buildTimeReportsSummaryCards } from "./timeReports.summary.mapper";

const toPositiveIntegerIds = (values: string[]): number[] => {
  const uniqueIds = new Set<number>();
  values.forEach((value) => {
    if (!/^\d+$/.test(value)) return;

    const parsedValue = Number(value);
    if (!Number.isSafeInteger(parsedValue) || parsedValue <= 0) {
      return;
    }

    uniqueIds.add(parsedValue);
  });

  return Array.from(uniqueIds);
};

type OverlayState =
  | { kind: "none" }
  | { kind: "options" }
  | { kind: "exportModal" };

export const TimeReports = (): JSX.Element => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [timeReportsV2State, timeReportsV2Actions] = useTimeReportsV2Store();
  const companyId = companyState.company?.id;

  const [monthDate, setMonthDate] = useState<LocalDate>(LocalDate.now());
  const [activeSummaryFilter, setActiveSummaryFilter] =
    useState<TimeReportsV2Filter | null>(null);
  const [overlay, setOverlay] = useState<OverlayState>({ kind: "none" });
  const [selectedWorkGroupIds, setSelectedWorkGroupIds] = useState<string[]>(
    [],
  );
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);
  const [selectedRangeStart, setSelectedRangeStart] =
    useState<LocalDate | null>(null);
  const [selectedRangeEnd, setSelectedRangeEnd] = useState<LocalDate | null>(
    null,
  );
  const [isAllRowsExpanded, setIsAllRowsExpanded] = useState(false);
  const [activeView, setActiveView] = useState<"person" | "staffRegister">(
    "person",
  );
  const [staffRegisterDate, setStaffRegisterDate] = useState<LocalDate>(
    LocalDate.now(),
  );
  const filterOptions = useCompanyFilterOptions({
    companyId,
    setSelectedWorkGroupIds,
  });

  const workGroupIds = useMemo<number[]>(
    () => toPositiveIntegerIds(selectedWorkGroupIds),
    [selectedWorkGroupIds],
  );
  const workerIds = useMemo<number[]>(
    () => toPositiveIntegerIds(selectedPersonIds),
    [selectedPersonIds],
  );
  const activeFilters = useMemo<TimeReportsV2Filter[] | undefined>(
    () => (activeSummaryFilter == null ? undefined : [activeSummaryFilter]),
    [activeSummaryFilter],
  );
  const selectedDateRange = useMemo<{
    fromDate: LocalDate;
    toDate: LocalDate;
  } | null>(() => {
    if (!selectedRangeStart || !selectedRangeEnd) return null;
    if (selectedRangeStart.isAfter(selectedRangeEnd)) {
      return { fromDate: selectedRangeEnd, toDate: selectedRangeStart };
    }
    return { fromDate: selectedRangeStart, toDate: selectedRangeEnd };
  }, [selectedRangeEnd, selectedRangeStart]);
  const groupByOption = timeReportsV2State.preferences.groupByOption;
  const queryScopeKey = useMemo<string>(() => {
    const monthFromDate = monthDate.withDayOfMonth(1).toString();
    const workGroupKey = [...workGroupIds].sort((a, b) => a - b).join(",");
    const workerKey = [...workerIds].sort((a, b) => a - b).join(",");
    const filterKey = activeSummaryFilter ?? "";
    const selectedDateKey = selectedDateRange
      ? `${selectedDateRange.fromDate.toString()}-${selectedDateRange.toDate.toString()}`
      : "";
    return [
      monthFromDate,
      groupByOption,
      filterKey,
      workGroupKey,
      workerKey,
      selectedDateKey,
    ].join("|");
  }, [
    activeSummaryFilter,
    groupByOption,
    monthDate,
    selectedDateRange,
    workGroupIds,
    workerIds,
  ]);

  const isPreferencesHydrated = useTimeReportsPreferencesSync({
    companyId,
    preferences: timeReportsV2State.preferences,
    loadTimeReportsV2Preferences:
      timeReportsV2Actions.loadTimeReportsV2Preferences,
    persistTimeReportsV2Preferences:
      timeReportsV2Actions.persistTimeReportsV2Preferences,
  });

  useEffect(() => {
    setIsAllRowsExpanded(false);
  }, [queryScopeKey]);

  useEffect(() => {
    setSelectedRangeStart(null);
    setSelectedRangeEnd(null);
  }, [monthDate]);

  useEffect(() => {
    setOverlay({ kind: "none" });
  }, [activeView]);

  const handleExpandAllClick = useCallback((): void => {
    setIsAllRowsExpanded((value) => !value);
  }, []);

  const handleOptionsOpen = useCallback((): void => {
    setOverlay({ kind: "options" });
  }, []);

  const handleOptionsClose = useCallback((): void => {
    setOverlay({ kind: "none" });
  }, []);

  const handleSummaryCardClick = useCallback(
    (card: TimeReportsSummaryCard): void => {
      setActiveSummaryFilter((current) =>
        current === card.filter ? null : card.filter,
      );
    },
    [],
  );

  const handleRangeChange = useCallback((dates: LocalDate[]): void => {
    if (dates.length === 0) {
      setSelectedRangeStart(null);
      setSelectedRangeEnd(null);
      return;
    }

    const sortedDates = [...dates].sort((a, b) => a.compareTo(b));
    setSelectedRangeStart(sortedDates[0] ?? null);
    setSelectedRangeEnd(sortedDates[sortedDates.length - 1] ?? null);
  }, []);

  const summaryCards = useMemo<TimeReportsSummaryCard[]>(
    () =>
      buildTimeReportsSummaryCards({
        activeSummaryFilter,
        formatSummaryTime: formatSummaryDuration,
        summary: timeReportsV2State.summary,
        t,
      }),
    [activeSummaryFilter, t, timeReportsV2State.summary],
  );

  return (
    <Layout>
      <TimeReportsToolbar
        unitTeamOptions={filterOptions.unitTeamOptions}
        selectedWorkGroupIds={selectedWorkGroupIds}
        personOptions={filterOptions.personOptions}
        unitTeamEmptyText={
          filterOptions.unitTeamLoadError
            ? t("OrganizationalStructure.UnitTeamLoadFailed")
            : t("OrganizationalStructure.NoUnitTeamMatches")
        }
        personEmptyText={
          filterOptions.workersLoadError
            ? t("OrganizationalStructure.WorkersLoadFailed")
            : t("OrganizationalStructure.NoPersonMatches")
        }
        selectedPersonIds={selectedPersonIds}
        monthDate={monthDate}
        selectedRangeStart={selectedRangeStart}
        selectedRangeEnd={selectedRangeEnd}
        isAllRowsExpanded={isAllRowsExpanded}
        onSelectedWorkGroupIdsChange={setSelectedWorkGroupIds}
        onSelectedPersonIdsChange={setSelectedPersonIds}
        onDateRangeChange={handleRangeChange}
        onExpandAllClick={handleExpandAllClick}
        onOptionsClick={handleOptionsOpen}
        showControls={activeView === "person"}
      />
      <ViewSelectorRow>
        <ViewTabs>
          <ViewTab
            type="button"
            $isActive={activeView === "person"}
            onClick={() => setActiveView("person")}
          >
            {t("TimeReports.Person")}
          </ViewTab>
          <ViewTab
            type="button"
            $isActive={activeView === "staffRegister"}
            onClick={() => setActiveView("staffRegister")}
          >
            {t("TimeReports.StaffRegister")}
          </ViewTab>
        </ViewTabs>
        {activeView === "staffRegister" && (
          <ViewSelectorActions>
            <SecondaryButton
              label={t("TimeReports.Export")}
              iconName="v2/download"
              size="small"
              onClick={() => setOverlay({ kind: "exportModal" })}
            />
          </ViewSelectorActions>
        )}
      </ViewSelectorRow>
      {activeView === "person" ? (
        <>
          <SummaryStrip>
            <PeriodSlot>
              <PeriodNavigator
                currentDate={monthDate}
                mode="month"
                onChange={setMonthDate}
                showSubLabel
                showYear
                showYearInMonthSubLabel={false}
                prevAriaLabel={t("TimeReports.PreviousPeriod")}
                nextAriaLabel={t("TimeReports.NextPeriod")}
              />
            </PeriodSlot>
            <TimeReportsSummaryCards
              cards={summaryCards}
              onCardClick={handleSummaryCardClick}
            />
          </SummaryStrip>
          <PersonView
            monthDate={monthDate}
            groupByOption={groupByOption}
            workGroupIds={workGroupIds}
            workerIds={workerIds}
            filters={activeFilters}
            dateRange={selectedDateRange}
            isAllRowsExpanded={isAllRowsExpanded}
            isPreferencesHydrated={isPreferencesHydrated}
            queryScopeKey={queryScopeKey}
          />
          <TimeReportsOptionsPanel
            isOpen={activeView === "person" && overlay.kind === "options"}
            groupByOption={groupByOption}
            onGroupByChange={timeReportsV2Actions.setGroupByOption}
            onClose={handleOptionsClose}
          />
        </>
      ) : (
        <>
          <StaffHeaderRow>
            <StaffHeaderDateCard>
              <PeriodSlot>
                <PeriodNavigator
                  currentDate={staffRegisterDate}
                  mode="day"
                  onChange={setStaffRegisterDate}
                  showSubLabel
                  prevAriaLabel={t("TimeReports.PreviousPeriod")}
                  nextAriaLabel={t("TimeReports.NextPeriod")}
                />
              </PeriodSlot>
            </StaffHeaderDateCard>
            <StaffHeaderCompanyCardSlot>
              <StaffRegisterCompanyCard />
            </StaffHeaderCompanyCardSlot>
          </StaffHeaderRow>
          <StaffRegisterView selectedDate={staffRegisterDate} />
          <StaffRegisterExportModal
            isOpen={
              activeView === "staffRegister" && overlay.kind === "exportModal"
            }
            selectedDate={staffRegisterDate}
            onClose={() => setOverlay({ kind: "none" })}
          />
        </>
      )}
    </Layout>
  );
};
